import React, { useState, useEffect } from 'react';
import { Search, Clock, ExternalLink, TrendingUp, BookOpen, Zap } from 'lucide-react';

const SubstackDiscovery = () => {
  const [keyword, setKeyword] = useState('');
  const [durations, setDurations] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allArticles, setAllArticles] = useState([]);
  const [loadingFeeds, setLoadingFeeds] = useState(true);

  // Curated list of quality Substack feeds
  const substackFeeds = [
    'https://www.startuparchive.org/feed',
    'https://www.lennysnewsletter.com/feed',
    'https://www.notboring.co/feed',
    'https://www.platformer.news/feed',
    'https://www.wheresyoured.at/feed',
    'https://www.readthegeneralist.com/feed',
    'https://www.stratechery.com/feed',
    'https://www.napkinmath.io/feed',
    'https://www.milkkarten.net/feed',
    'https://www.constructionphysics.com/feed',
  ];

  // Fetch articles from Substack feeds on component mount
  useEffect(() => {
    const fetchAllFeeds = async () => {
      const allFetchedArticles = [];
      let successCount = 0;

      for (const feedUrl of substackFeeds) {
        try {
          console.log(`Fetching ${feedUrl}...`);
          
          // Using CORS proxy with timeout
          const corsProxy = 'https://api.allorigins.win/get?url=';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(corsProxy + encodeURIComponent(feedUrl), {
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          
          const data = await response.json();
          
          // Parse XML manually
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(data.contents, 'text/xml');
          
          const items = xmlDoc.querySelectorAll('item');
          const feedTitle = xmlDoc.querySelector('channel > title')?.textContent || 'Unknown';
          
          console.log(`Found ${items.length} items from ${feedTitle}`);
          
          items.forEach((item, index) => {
            if (index >= 10) return; // Limit to 10 articles per feed
            
            const title = item.querySelector('title')?.textContent || '';
            const link = item.querySelector('link')?.textContent || '';
            const description = item.querySelector('description')?.textContent || '';
            const pubDate = item.querySelector('pubDate')?.textContent || '';
            
            // Extract plain text from HTML description
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = description;
            const preview = tempDiv.textContent.slice(0, 200).trim() + '...';
            
            // Estimate read time (avg 200 words per minute)
            const wordCount = tempDiv.textContent.split(/\s+/).length;
            const readTime = Math.max(1, Math.ceil(wordCount / 200));

            // Extract categories/tags
            const categories = Array.from(item.querySelectorAll('category')).map(cat => cat.textContent);

            allFetchedArticles.push({
              title: title,
              author: feedTitle,
              publication: feedTitle,
              url: link,
              preview: preview,
              readTime: readTime,
              tags: categories,
              pubDate: pubDate
            });
          });
          
          successCount++;
          setAllArticles([...allFetchedArticles]); // Update state incrementally
          
        } catch (error) {
          console.error(`Error fetching ${feedUrl}:`, error.message);
        }
      }

      console.log(`Successfully loaded ${successCount}/${substackFeeds.length} feeds`);
      console.log(`Total articles: ${allFetchedArticles.length}`);
      setLoadingFeeds(false);
    };

    fetchAllFeeds();
  }, []);

  // Mock data - in production, this would come from Substack RSS/API
  const mockArticles = [
    {
      title: "The Hidden Economics of Attention",
      author: "Sarah Chen",
      publication: "Mind & Markets",
      url: "#",
      preview: "Why your focus is the most valuable commodity in the digital age, and how companies are competing for every second of it...",
      readTime: 5,
      tags: ["economics", "psychology", "technology"]
    },
    {
      title: "Building in Public: Month 3",
      author: "Alex Rivera",
      publication: "Indie Hacker Daily",
      url: "#",
      preview: "Revenue hit $2.3k this month. Here's what worked, what failed, and the pivot that changed everything...",
      readTime: 8,
      tags: ["startups", "business", "indie"]
    },
    {
      title: "The Art of Deep Work in a Shallow World",
      author: "Marcus Zhang",
      publication: "Focus Lab",
      url: "#",
      preview: "A practical framework for maintaining concentration when the entire internet wants your attention...",
      readTime: 12,
      tags: ["productivity", "psychology", "career"]
    },
    {
      title: "Why Most Side Projects Fail",
      author: "Emma Thompson",
      publication: "Builder's Log",
      url: "#",
      preview: "Analyzed 500 failed side projects. The pattern was clear: it wasn't about the idea...",
      readTime: 6,
      tags: ["startups", "business", "productivity"]
    },
    {
      title: "The Neuroscience of Scrolling",
      author: "Dr. James Park",
      publication: "Brain Bytes",
      url: "#",
      preview: "What happens in your brain during a doomscroll session, explained with recent fMRI studies...",
      readTime: 15,
      tags: ["psychology", "technology", "science"]
    },
    {
      title: "Monetizing Attention: Ethical or Not?",
      author: "Lisa Kumar",
      publication: "Tech Ethics Weekly",
      url: "#",
      preview: "The moral implications of the attention economy and what builders should consider...",
      readTime: 10,
      tags: ["economics", "technology", "ethics"]
    },
    {
      title: "The Renaissance of Digital Art",
      author: "Maya Johnson",
      publication: "Creative Weekly",
      url: "#",
      preview: "How NFTs and AI are reshaping what it means to be an artist in 2025...",
      readTime: 7,
      tags: ["arts", "creativity", "technology"]
    },
    {
      title: "Writing as Therapy: A Creative Outlet",
      author: "David Lee",
      publication: "Mindful Creator",
      url: "#",
      preview: "Exploring how creative writing helps process emotions and build mental resilience...",
      readTime: 9,
      tags: ["writing", "arts", "psychology"]
    },
    {
      title: "The Business of Being Creative",
      author: "Sophie Turner",
      publication: "Artist Economics",
      url: "#",
      preview: "Making a living from your art without selling out - practical strategies that work...",
      readTime: 11,
      tags: ["arts", "business", "creativity"]
    }
  ];

  const durationOptions = [
    { label: "Quick (< 5 min)", value: "quick", max: 5 },
    { label: "Medium (5-10 min)", value: "medium", min: 5, max: 10 },
    { label: "Deep (> 10 min)", value: "deep", min: 10 }
  ];

  const toggleDuration = (value) => {
    setDurations(prev => 
      prev.includes(value) 
        ? prev.filter(d => d !== value)
        : [...prev, value]
    );
  };

  // AI-ENHANCED: Simple semantic term expansion
  const getRelatedTerms = (query) => {
    const semanticMap = {
      'startups': ['startups', 'indie', 'entrepreneur', 'founder', 'business', 'launch', 'mvp'],
      'psychology': ['psychology', 'mental', 'brain', 'behavior', 'cognitive', 'mind'],
      'money': ['money', 'economics', 'finance', 'wealth', 'income', 'revenue'],
      'productivity': ['productivity', 'focus', 'deep work', 'efficiency', 'time management'],
      'technology': ['technology', 'tech', 'ai', 'software', 'digital', 'internet'],
      'writing': ['writing', 'content', 'storytelling', 'essays', 'articles'],
      'arts': ['arts', 'art', 'creative', 'creativity', 'design', 'artist'],
    };

    // Find matching semantic group
    for (const [key, terms] of Object.entries(semanticMap)) {
      if (terms.some(term => query.includes(term) || term.includes(query))) {
        return terms;
      }
    }
    
    return [query]; // fallback to original
  };

  const handleSearch = () => {
    setLoading(true);
    
    setTimeout(() => {
      let filtered = allArticles.length > 0 ? allArticles : mockArticles;

      // AI-ENHANCED: Semantic search with related terms
      if (keyword.trim()) {
        const kw = keyword.toLowerCase();
        const relatedTerms = getRelatedTerms(kw);
        
        filtered = filtered.filter(article => {
          const searchableText = `${article.title} ${article.preview} ${article.tags.join(' ')}`.toLowerCase();
          return relatedTerms.some(term => searchableText.includes(term));
        });
      }

      // Filter by duration
      if (durations.length > 0) {
        filtered = filtered.filter(article => {
          return durations.some(d => {
            const option = durationOptions.find(opt => opt.value === d);
            if (option.max && option.min) {
              return article.readTime >= option.min && article.readTime <= option.max;
            } else if (option.max) {
              return article.readTime <= option.max;
            } else if (option.min) {
              return article.readTime >= option.min;
            }
            return true;
          });
        });
      }

      // Get random 3 articles
      const shuffled = filtered.sort(() => 0.5 - Math.random());
      setResults(shuffled.slice(0, 3));
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-12 pt-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-8 h-8 text-indigo-600" />
            <h1 className="text-4xl font-bold text-slate-900">ReadThree</h1>
          </div>
          <p className="text-slate-600 text-lg">Stop doomscrolling. Start reading what matters.</p>
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          {loadingFeeds && (
            <div className="mb-4 text-center">
              <div className="inline-flex items-center gap-2 text-indigo-600 bg-indigo-50 px-4 py-2 rounded-full">
                <div className="animate-spin w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                Loading {allArticles.length} articles from Substack...
              </div>
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              What do you want to read about?
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g., startups, psychology, economics..."
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-none text-lg"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              How much time do you have?
            </label>
            <div className="flex flex-wrap gap-3">
              {durationOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => toggleDuration(option.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    durations.includes(option.value)
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>Loading...</>
            ) : (
              <>
                <TrendingUp className="w-5 h-5" />
                Find My Reads
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-indigo-600" />
                Your Curated Reads
              </h2>
              <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                <Zap className="w-4 h-4" />
                AI-enhanced search
              </div>
            </div>
            {results.map((article, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow border border-slate-100"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-1">
                      {article.author} · {article.publication}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full text-sm font-medium ml-4">
                    <Clock className="w-4 h-4" />
                    {article.readTime} min
                  </div>
                </div>
                
                <p className="text-slate-700 mb-4 leading-relaxed">
                  {article.preview}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {article.tags.map(tag => (
                      <span
                        key={tag}
                        className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={article.url}
                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Read Now
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && results.length === 0 && keyword && (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <p className="text-slate-600 text-lg">
              No articles found. Try different keywords or time filters!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubstackDiscovery;
